import React, { useState } from 'react';
import { ProjectData } from '../../types';
import { Wand2, X, Check, Palette } from 'lucide-react';

interface CoverDesignSelectorProps {
  currentDesignId: string;
  themeColor: string;
  projectData: ProjectData;
  onSelectDesign: (id: string) => void;
  onSelectColor: (color: string) => void;
  onClose: () => void;
}

export const DESIGNS = [
  { id: 'classic', label: 'Klassisch' },
  { id: 'modern', label: 'Modern Split' },
  { id: 'minimal', label: 'Minimalistisch' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'architect', label: 'Architekt' },
  { id: 'tech', label: 'Technisch' },
  { id: 'swiss', label: 'Swiss Grid' },
  { id: 'elegant', label: 'Elegant' },
  { id: 'bauhaus', label: 'Bauhaus' },
  { id: 'blueprint', label: 'Blaupause' },
  { id: 'nature', label: 'Natur & Eco' },
  { id: 'studio', label: 'Studio' },
  { id: 'urban', label: 'Urban' },
  { id: 'poly', label: 'Polygon' },
  { id: 'card', label: 'Karte' },
  { id: 'horizon', label: 'Horizont' },
  { id: 'editorial', label: 'Magazin' },
  { id: 'compact', label: 'Kompakt' },
];

export const CoverDesignSelector: React.FC<CoverDesignSelectorProps> = ({
  currentDesignId,
  themeColor,
  projectData,
  onSelectDesign,
  onSelectColor,
  onClose
}) => {
  const [isExtracting, setIsExtracting] = useState(false);

  const extractColorFromLogo = () => {
    if (!projectData.companyLogo) {
      alert("Bitte laden Sie zuerst ein Firmenlogo im Reiter 'Projekt' hoch.");
      return;
    }

    setIsExtracting(true);
    const img = new Image();
    img.src = projectData.companyLogo;
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = 100;
      canvas.height = 100;
      ctx.drawImage(img, 0, 0, 100, 100);

      const imageData = ctx.getImageData(0, 0, 100, 100);
      const data = imageData.data;
      
      let r = 0, g = 0, b = 0, count = 0;

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha > 200) {
           const tr = data[i];
           const tg = data[i + 1];
           const tb = data[i + 2];
           
           const brightness = (tr + tg + tb) / 3;
           if (brightness < 240 && brightness > 20) {
             r += tr;
             g += tg;
             b += tb;
             count++;
           }
        }
      }

      if (count > 0) {
        r = Math.floor(r / count);
        g = Math.floor(g / count);
        b = Math.floor(b / count);
        
        const hex = "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
        onSelectColor(hex);
      } else {
        alert("Konnte keine dominante Farbe erkennen.");
      }
      setIsExtracting(false);
    };

    img.onerror = () => {
      alert("Fehler beim Verarbeiten des Logos.");
      setIsExtracting(false);
    };
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-200">
      <div className="bg-white w-full max-w-4xl h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col relative">
        
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Deckblatt Design</h2>
            <p className="text-xs text-slate-500">Wählen Sie ein Layout für Ihre Dokumentation.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          
          <div className="w-full md:w-64 bg-slate-50 p-5 border-r border-slate-200 overflow-y-auto">
            <div className="mb-6">
              <label className="text-xs font-bold text-slate-500 uppercase mb-2 block flex items-center gap-2">
                <Palette className="w-4 h-4" /> Hauptfarbe
              </label>
              <div className="flex items-center gap-3 mb-3">
                <input 
                  type="color" 
                  value={themeColor} 
                  onChange={(e) => onSelectColor(e.target.value)} 
                  className="w-12 h-12 rounded cursor-pointer border-0 p-0 shadow-sm"
                />
                <div className="text-xs font-mono bg-white px-2 py-1 rounded border border-slate-200">
                  {themeColor}
                </div>
              </div>

              <button 
                onClick={extractColorFromLogo}
                disabled={isExtracting}
                className="w-full flex items-center justify-center gap-2 bg-white border border-slate-300 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 text-slate-600 text-xs font-bold py-2 px-3 rounded-lg transition-all shadow-sm"
              >
                 {isExtracting ? <Wand2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                 Farbe aus Logo
              </button>
              {!projectData.companyLogo && (
                 <p className="text-[11px] text-slate-400 mt-2 leading-tight">
                    Laden Sie ein Logo im Projekt-Tab hoch, um diese Funktion zu nutzen.
                 </p>
              )}
            </div>
            
            <div className="h-px bg-slate-200 w-full mb-6"></div>
            
            <div className="text-xs text-slate-500 leading-relaxed">
              <strong>Tipp:</strong> Das gewählte Design wird nur auf dem Deckblatt angewendet. Die Inhaltsseiten bleiben neutral und übersichtlich, verwenden aber die gewählte Farbe für Akzente.
            </div>
          </div>

          <div className="flex-1 bg-slate-100/50 p-6 overflow-y-auto">
             <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {DESIGNS.map((design) => {
                  const isSelected = currentDesignId === design.id;
                  
                  return (
                    <div 
                      key={design.id}
                      onClick={() => onSelectDesign(design.id)}
                      className={`group cursor-pointer relative rounded-xl transition-all duration-200 overflow-hidden border-2 ${isSelected ? 'border-blue-600 shadow-xl scale-[1.02]' : 'border-slate-200 hover:border-blue-300 shadow-sm hover:shadow-md bg-white'}`}
                    >
                       <div className="aspect-[210/297] bg-white relative p-3 flex flex-col pointer-events-none select-none">
                          {renderMiniPreview(design.id, themeColor)}
                       </div>

                       <div className="absolute bottom-0 left-0 w-full bg-white/90 backdrop-blur p-2 border-t border-slate-100 flex justify-between items-center">
                          <span className={`text-xs font-bold ${isSelected ? 'text-blue-600' : 'text-slate-700'}`}>
                             {design.label}
                          </span>
                          {isSelected && <div className="bg-blue-600 text-white rounded-full p-0.5"><Check className="w-3 h-3" /></div>}
                       </div>
                    </div>
                  );
                })}
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const renderMiniPreview = (id: string, color: string) => {
   const commonImg = <div className="w-full h-full bg-slate-200 rounded-sm overflow-hidden"><div className="w-full h-full opacity-50 bg-slate-300 flex items-center justify-center text-[10px] text-slate-400">BILD</div></div>;
   const commonText = <div className="space-y-1"><div className="h-2 w-3/4 bg-slate-800 rounded-sm"></div><div className="h-1 w-1/2 bg-slate-400 rounded-sm"></div></div>;
   
   switch(id) {
      case 'modern':
         return (
            <div className="h-full flex flex-col">
               <div className="h-1/2 bg-slate-100 p-2">{commonImg}</div>
               <div className="h-1/2 p-2 relative">
                  <div className="absolute top-0 left-2 w-4 h-1" style={{backgroundColor: color}}></div>
                  <div className="mt-4">{commonText}</div>
               </div>
            </div>
         );
      case 'minimal':
         return (
             <div className="h-full flex flex-col items-center justify-center p-4 text-center">
                 <div className="w-16 h-16 mb-2 rounded-full overflow-hidden">{commonImg}</div>
                 <div className="h-1.5 w-20 bg-slate-800 mb-1"></div>
                 <div className="h-0.5 w-10 bg-slate-400 mx-auto"></div>
             </div>
         );
      case 'corporate':
         return (
             <div className="h-full flex">
                 <div className="w-1/4 h-full" style={{backgroundColor: color}}></div>
                 <div className="w-3/4 p-2 flex flex-col">
                     <div className="h-24 mb-2">{commonImg}</div>
                     {commonText}
                 </div>
             </div>
         );
      case 'architect':
         return (
             <div className="h-full p-2 border-4 border-slate-900 flex flex-col justify-between">
                 <div className="h-1/2 border border-slate-200 p-1">{commonImg}</div>
                 <div>
                    <div className="h-1 w-full mb-1" style={{backgroundColor: color}}></div>
                    {commonText}
                 </div>
             </div>
         );
      case 'tech':
         return (
             <div className="h-full p-2 font-mono">
                 <div className="border border-slate-300 h-full p-1 relative">
                    <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-slate-400"></div>
                    <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-slate-400"></div>
                    <div className="mb-2 text-[10px] tracking-widest text-slate-400">001-A</div>
                    {commonText}
                 </div>
             </div>
         );
      default:
         return (
            <div className="h-full p-2 flex flex-col">
               <div className="h-3/4 bg-slate-100 p-2 mb-2">{commonImg}</div>
               <div className="border-l-2 pl-2" style={{borderColor: color}}>{commonText}</div>
            </div>
         );
   }
};